import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import DataTable from "react-data-table-component";
import Select from "react-select";
import { useFormik } from "formik";
import * as yup from "yup";
import jsPDF from "jspdf";
import moment from "moment";

/* COMPONENTS */
import Input from "../../components/input";

/* MATERIAL TAILWIND COMPONENTS */
import {
  Button,
  Dialog,
  DialogBody,
  Tooltip,
  Card,
  CardBody,
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
} from "@material-tailwind/react";

/* ICONS */
import { FiTrash } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { FaDownload } from "react-icons/fa";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* API IMPORT */
import {
  getAwardApplications,
  deleteAwardApplicationById,
} from "../../service/api/contactUs.api";

/* CONFIG */
import { IMAGE_URL } from "../../config";
import ExcelExport from "components/export";
import { GoKebabHorizontal } from "react-icons/go";
import {
  sendEmailToSelected,
  updateApplication,
  updateUserStatusJury,
  updateUserStatusOffice,
} from "service/api/auth.api";
import "../../assets/IBMPlexSans-bold";
import "../../assets/IBMPlexSans-normal";

const exportInstitutePDF = async (application) => {
  const getBase64FromUrl = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const lineHeight = 8;
  const sectionPadding = 5;
  const sectionTopPadding = 5;
  let y = margin;

  const maxWidth = pageWidth - 2 * margin;

  // ✅ Page break checker
  const checkPageBreak = (requiredHeight = lineHeight) => {
    if (y + requiredHeight > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // === HEADER IMAGE ===
  const logoBase64 = await getBase64FromUrl(
    "https://storage.googleapis.com/cdn-roundtechsquare/lohana-mahaparishad/form-header.png"
  );
  const tempImg = new Image();
  tempImg.src = logoBase64;

  await new Promise((resolve) => {
    tempImg.onload = resolve;
  });

  const imgRatio = tempImg.height / tempImg.width;
  const imgWidth = pageWidth;
  const imgHeight = imgWidth * imgRatio;

  const labelX = margin + sectionPadding;
  const valueX = margin + 70;

  checkPageBreak(imgHeight);
  doc.addImage(logoBase64, "PNG", 0, y, imgWidth, imgHeight);
  y += imgHeight;

  const addLine = (label, value) => {
    checkPageBreak(lineHeight * 2);

    // Wrap long values
    const wrappedValue = doc.splitTextToSize(
      value || "N/A",
      maxWidth - (valueX - margin)
    );
    const requiredHeight = wrappedValue.length * lineHeight;

    // Label
    doc.setFont("IBMPlexSans", "bold");
    doc.text(`${label}:`, labelX, y);

    // Value
    doc.setFont("IBMPlexSans", "normal");
    doc.text(wrappedValue, valueX, y);

    // Move Y correctly (prevent overlap)
    y += requiredHeight + 4; // extra padding
  };

  const addWrappedLink = (label, url, displayText = "View Document") => {
    if (!url) return;
    checkPageBreak(lineHeight * 2);
    doc.setFont("IBMPlexSans", "bold");
    doc.text(`${label}:`, labelX, y);
    doc.setFont("IBMPlexSans", "normal");
    const linkWidth = doc.getTextWidth(displayText);
    doc.setTextColor(0, 0, 255);
    doc.text(displayText, valueX, y);
    doc.link(valueX, y - lineHeight + 2, linkWidth, lineHeight, { url });
    doc.setTextColor(0, 0, 0);
    y += lineHeight + 4;
  };

  const getFileUrl = (path) => (path ? `${IMAGE_URL}${path}` : null);

  const fullName = `${application.firstName || ""} ${
    application.middleName || ""
  } ${application.lastName || ""}`.trim();

  const addSection = (title, drawContentCallback) => {
    checkPageBreak(lineHeight + 12);
    doc.setFontSize(13);
    doc.setFont("IBMPlexSans", "bold");
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, y, maxWidth, lineHeight + 6, "F");
    doc.text(title, labelX, y + lineHeight);
    y += lineHeight + 14;
    doc.setFontSize(11);
    doc.setFont("IBMPlexSans", "bold");
    drawContentCallback();
    y += 6;
  };

  // === INSTITUTE INFO ===
  const inst = application.institute;
  if (inst) {
    addSection("Institute Information", () => {
      addLine("Institute Name", inst.name);
      addLine(
        "Contact Person Name",
        inst.firstName + " " + inst.middleName + " " + inst.lastName
      );
      addLine("Mobile Number", inst.mobileNumber);
      addLine("Email", inst.email);
      addLine("Date of Establishment", inst.dateOfEstablishment);
      addLine("Lohana Connect ID", application.lohanaConnectId);
      addWrappedLink("Signature", getFileUrl(application.applicantSignature));
      addLine("Office Address", inst.officeAddress);
      addLine("Campus Address", inst.campusAddress);
      addLine("State", inst.state);
      addLine("District", inst.district);
      addLine("Zone", inst.zone);
      addLine("Mahajan", inst.mahajan);
      addLine("Type of Institute", inst.typeOfInstitute);
      addLine("Affiliating Body", inst.affiliatingBody);
      addLine("Accreditation", inst.accreditation);
      addLine("Number of Students", inst.numberOfStudentsStudying);
      addLine("Youtube URL", inst.youtubeURL);
      addLine("Profile Summary", inst.profileSummary);
      addWrappedLink("Photograph", getFileUrl(inst.photograph));
      addWrappedLink("Detailed Profile", getFileUrl(inst.detailedProfile));
    });
  }

  // === SUPPORTING DOCUMENTS ===
  const docs = application.documents;
  if (docs) {
    addSection("Supporting Documents", () => {
      addWrappedLink(
        "Copy of Registration Certificate",
        getFileUrl(docs.copyOfRegistrationCertificate)
      );
    });
  }

  doc.save(`${inst?.name || "Institute"}_Application.pdf`);
};

const types = [
  { value: "school", label: "School" },
  { value: "college", label: "College" },
  { value: "university", label: "University" },
];

const zones = [
  "Saurashtra 1",
  "Saurashtra 2",
  "Saurashtra 3",
  "Saurashtra 4",
  "Kutch",
  "North Gujarat",
  "Central Gujarat",
  "South Gujarat",
  "Mumbai",
  "Maharashtra",
  "Central India",
  "Vidarbha",
  "South India",
  "East India",
  "North India",
];

const statesOfIndia = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const officeBearerOptions = [
  { value: "eligible", label: "Eligible" },
  { value: "ineligible", label: "Ineligible" },
  { value: "rejected", label: "Rejected" },
];

const officeBearerOptionsUpdated = [
  { value: "eligible", label: "Eligible" },
  { value: "ineligible", label: "Ineligible" },
  { value: "rejected", label: "Rejected" },
  { value: "shortlisted", label: "Shortlisted" },
];

const juryOptions = [
  { value: "selected", label: "Selected" },
  { value: "not-selected", label: "Not Selected" },
];

const statusOptionsForSuperAdmin = [
  { value: null, label: "All status" },
  { value: "pending", label: "Pending" },
  { value: "eligible", label: "Eligible" },
  { value: "rejected", label: "Rejected" },
  { value: "ineligible", label: "Ineligible" },
  { value: "selected", label: "Selected" },
  { value: "not-selected", label: "Not Selected" },
  { value: "shortlisted", label: "Shortlisted" },
];

const statusOptionsForJury = [
  { value: "eligible", label: "Eligible" },
  { value: "ineligible", label: "Ineligible" },
  { value: "selected", label: "Selected" },
  { value: "not-selected", label: "Not Selected" },
  { value: "shortlisted", label: "Shortlisted" },
];

const statusOptionsForOfficeBearer = [
  { value: null, label: "All status" },
  { value: "pending", label: "Pending" },
  { value: "eligible", label: "Eligible" },
  { value: "ineligible", label: "Ineligible" },
  { value: "rejected", label: "Rejected" },
  { value: "shortlisted", label: "Shortlisted" },
];

const validationSchema = yup.object().shape({
  applicationStatus: yup.object().required("Status is required"),
});

const Institute = () => {
  /* STATE VARIABLES */
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(100);

  /* LIST OF DATA */
  const [dataList, setDataList] = useState([]);

  /* TOTAL DOCS */
  const [totalDocs, setTotalDocs] = useState(0);

  /* SEARCH VALUE */
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  /* DELETE ID */
  const [deleteId, setDeleteId] = useState(null);

  /* DELETE MODAL */
  const [deleteModal, setDeleteModal] = useState(false);

  /* SELECTED APPLICATION */
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailsModal, setDetailsModal] = useState(false);

  /* STATE OPTIONS */
  const [stateOptions, setStateOptions] = useState([]);

  /* ZONE OPTIONS */
  const [zoneOptions, setZoneOptions] = useState([]);

  /* SELECTED STATE AND ZONE */
  const [selectedState, setSelectedState] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);

  /* EXPORTED DATA */
  const [exportData, setExportData] = useState([]);

  /* STATUS DIALOG */
  const [statusDialog, setStatusDialog] = useState(false);
  const [juryStatusDialog, setJuryStatusDialog] = useState(false);

  /* USER ROLE */
  const [userRole, setUserRole] = useState("");

  /* STATUS LOADING */
  const [statusLoading, setStatusLoading] = useState(false);

  /* SELECTED ROLE */
  const [selectedRole, setSelectedRole] = useState(null);

  /* SELECTED ID */
  const [selectedId, setSelectedId] = useState();

  /* REWARDS DIALOG */
  const [rewardsDialog, setRewardsDialog] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);

  /* DEFAULT OPTION */
  const [defaultSelected, setDefaultSelected] = useState(null);

  /* SEND EMAIL DIALOG */
  const [sendEmailDialog, setSendEmailDialog] = useState(false);

  /* EDIT MODAL DIALOG */
  const [editModal, setEditModal] = useState(false);
  const [isShortlistedValue, setIsShortlistedValue] = useState("");

  /* GET LIST OF DATA */
  const getDataListFunction = () => {
    setLoading(true);
    const params = {
      page: 1,
      sizePerPage: 1000,
      pagination: true,
      state: selectedState === null ? "" : selectedState?.value,
      zone: selectedZone === null ? "" : selectedZone?.value,
    };

    if (defaultSelected !== null && defaultSelected?.value !== "shortlisted") {
      params.applicationStatus = defaultSelected?.value;
    } else if (defaultSelected?.value === "shortlisted") {
      params.applicationStatus = "eligible";
      params.isShortListed = true;
    }

    getAwardApplications(params)
      .then((res) => {
        let allData = res?.data?.docs || [];

        allData = allData.filter((item) => {
          return item.institute && item.institute.name;
        });
        if (search.trim()) {
          const searchLower = search.toLowerCase();

          allData = allData.filter((item) => {
            const instituteName = item.institute.name
              ? item.institute.name.toLowerCase()
              : "";
            const instituteFullName = `${item.institute.firstName || ""} ${
              item.institute.middleName || ""
            } ${item.institute.lastName || ""}`.toLowerCase();
            const instituteEmail = item.institute.email
              ? item.institute.email.toLowerCase()
              : "";

            return (
              instituteName.includes(searchLower) ||
              instituteFullName.includes(searchLower) ||
              instituteEmail.includes(searchLower)
            );
          });
        }
        const startIndex = (page - 1) * sizePerPage;
        const endIndex = startIndex + sizePerPage;
        const paginatedData = allData.slice(startIndex, endIndex);
        setDataList(paginatedData);
        setTotalDocs(allData.length);
      })
      .catch((e) => {
        console.error("Error in component:", e);
        toast.error("Failed to fetch data");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* EXCEL EXPORT */
  const getExportList = () => {
    return new Promise((resolve, reject) => {
      setLoading(true);
      const params = {
        page: 1,
        sizePerPage: 1000,
        pagination: true,
        state: selectedState === null ? "" : selectedState?.value,
        zone: selectedZone === null ? "" : selectedZone?.value,
      };

      if (
        defaultSelected !== null &&
        defaultSelected?.value !== "shortlisted"
      ) {
        params.applicationStatus = defaultSelected?.value;
      } else if (defaultSelected?.value === "shortlisted") {
        params.applicationStatus = "eligible";
        params.isShortListed = true;
      }

      getAwardApplications(params)
        .then((res) => {
          let allData = res?.data?.docs || [];

          /* FILTER ONLY TEACHER DATA */
          allData = allData.filter((item) => {
            return item.institute && item.institute.name;
          });

          const tempArray = allData?.map((item, index) => {
            return {
              "Sr. No:": index + 1,
              "Contact Person Name": `${item.institute.firstName || ""} ${
                item.institute.middleName || ""
              } ${item.institute.lastName || ""}`,
              "Mobile Number": item.institute?.mobileNumber ?? "N/A",
              "Email address": item.institute?.email ?? "N/A",
              "Date of Application": item?.dateOfApplication ?? "N/A",
              Accreditation: item.institute?.accreditation ?? "N/A",
              "Affiliating body": item.institute?.affiliatingBody ?? "N/A",
              "Campus Address": item.institute?.campusAddress ?? "N/A",
              "Date of Establishment":
                item.institute?.dateOfEstablishment ?? "N/A",
              "Detailed Profile": `https://storage.googleapis.com/kidsstreet-test/${item?.institute?.detailedProfile}`,
              State: item?.institute?.state ?? "N/A",
              District: item?.institute?.district ?? "N/A",
              Zone: item?.institute?.zone ?? "N/A",
              Mahajan: item?.institute?.mahajan ?? "N/A",
              Email: item?.institute?.email ?? "N/A",
              "Number of students studying":
                item?.institute?.numberOfStudentsStudying ?? "N/A",
              "Office Address": item?.institute?.officeAddress ?? "N/A",
              Photograph: `https://storage.googleapis.com/kidsstreet-test/${item?.institute?.photograph}`,
              "Profile Summary": item?.institute?.profileSummary ?? "N/A",
              "Type of institute": item?.institute?.typeOfInstitute ?? "N/A",
              "Youtube URL": item?.institute?.youtubeURL ?? "N/A",
              "Application Date": item?.dateOfApplication ?? "N/A",
              "Lohana Connect ID": item?.lohanaConnectId ?? "N/A",
              "Date of birth": item?.dateOfBirth ?? "N/A",
              "Copy of Registration Certificate": `https://storage.googleapis.com/kidsstreet-test/${item?.registrationCertificate}`,
              "Applicant Signature": `https://storage.googleapis.com/kidsstreet-test/${item?.applicantSignature}`,

              /* STATUS */
              "Application Status":
                defaultSelected?.value === "shortlisted"
                  ? "Eligible & Shortlisted"
                  : item?.applicationStatus,

              /* OFFICE BEARER REMARKS */
              "Office Bearer Remarks":
                (item.officeBearerRemarks || [])
                  .map(
                    (r) =>
                      `${r.remark} (by ${r.addedBy?.firstName || ""} ${
                        r.addedBy?.lastName || ""
                      } on ${moment(r.createdAt).format("DD MMM")})`
                  )
                  .join(", ") || "No remarks",

              /* JURY BEARER REMARKS */
              "Jury Remarks":
                (item.juryRemarks || [])
                  .map(
                    (r) =>
                      `${r.remark} (by ${r.addedBy?.firstName || ""} ${
                        r.addedBy?.lastName || ""
                      } on ${moment(r.createdAt).format("DD MMM")})`
                  )
                  .join(", ") || "No remarks",
            };
          });

          setExportData(tempArray);
          resolve(tempArray);
        })
        .catch((e) => {
          console.error("Export data fetch error:", e);
          toast.error("Failed to fetch export data");
          reject(e);
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  /* FUNCTIONS */
  const handlePageChange = async (e) => {
    setPage(e);
    //getDataListFunction();
  };

  /* HANDLE PER PAGE */
  const handlePerPageChange = async (e) => {
    setSizePerPage(e);
    //getDataListFunction();
  };

  /* HANDLE DELETE DATA */
  const handleDelete = () => {
    setLoading(true);
    deleteAwardApplicationById(deleteId)
      .then((res) => {
        toast.success(res?.message || "Application deleted successfully");
        setDataList((prev) => prev.filter((item) => item._id !== deleteId));
      })
      .catch((e) => {
        console.error("Error deleting application:", e);
        toast.error(
          e?.response?.data?.message || "Failed to delete application"
        );
      })
      .finally(() => {
        setDeleteModal(false);
        setDeleteId(null);
        setLoading(false);
      });
  };

  /* HANDLE SEND EMAIL */
  const handleSendEmail = () => {
    setLoading(true);
    sendEmailToSelected(deleteId)
      .then((res) => {
        toast.success(res?.message || "The email has been sent successfully");
      })
      .catch((e) => {
        console.error("Error deleting application:", e);
        toast.error(
          e?.response?.data?.message || "Failed to delete application"
        );
      })
      .finally(() => {
        setSendEmailDialog(false);
        setDeleteId(null);
        setLoading(false);
      });
  };

  /* GET INSTITUTE'S FULL NAME */
  const getInstituteName = (application) => {
    const { firstName, middleName, lastName } = application.institute;
    return `${firstName || ""} ${middleName || ""} ${lastName || ""}`.trim();
  };

  /* VIEW APPLICATION DETAILS */
  const viewApplicationDetails = (application) => {
    setSelectedApplication(application);
    setDetailsModal(true);
  };

  /* FORMAT DATE */
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return "Invalid date";
    }
  };

  /* GET FULL FILE URL */
  const getFullFileUrl = (path) => {
    if (!path) return null;
    return `${IMAGE_URL}${path}`;
  };

  /* UPDATE FORMIK */
  const updateFormik = useFormik({
    initialValues: {
      applicationStatus: "",
      remarks: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setStatusLoading(true);
      let payload = {};

      if (values?.applicationStatus?.value === "shortlisted") {
        payload = {
          isShortListed: true,
          applicationStatus: "eligible",
        };
      } else if (values?.remarks !== "") {
        payload = {
          applicationStatus: values?.applicationStatus?.value,
          remarks: values?.remarks,
        };
      } else if (values?.remarks === "") {
        payload = {
          applicationStatus: values?.applicationStatus?.value,
        };
      } else {
        console.log("Not received payload");
      }

      updateUserStatusOffice(selectedId, payload)
        .then((res) => {
          if (res?.status) {
            toast.success("The status has been updated successfully");
            setStatusDialog(!statusDialog);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          console.log("Error: ", e);
        })
        .finally(() => {
          setStatusLoading(!statusLoading);
          getDataListFunction();
          updateFormik.resetForm();
        });
    },
  });

  /* UPDATE FORMIK JURY */
  const updateFormikJury = useFormik({
    initialValues: {
      applicationStatus: "",
      remarks: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setStatusLoading(true);
      let payload = {};

      if (values?.remarks !== "") {
        payload = {
          applicationStatus: values?.applicationStatus?.value,
          remarks: values?.remarks,
        };
      }

      if (values?.remarks === "") {
        payload = {
          applicationStatus: values?.applicationStatus?.value,
        };
      }

      updateUserStatusJury(selectedId, payload)
        .then((res) => {
          if (res?.status) {
            toast.success("The status has been updated successfully");
            setJuryStatusDialog(!juryStatusDialog);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          console.log("Error: ", e);
        })
        .finally(() => {
          setStatusLoading(!statusLoading);
          getDataListFunction();
          updateFormikJury.resetForm();
        });
    },
  });

  /* HANDLE STATUS DIALOG */
  const handleStatusDialogOpen = (row) => {
    setSelectedId(row?._id);
    const user = JSON.parse(localStorage.getItem("user"));
    setIsShortlistedValue(row?.applicationStatus);

    if (row?.applicationStatus && row?.applicationStatus !== "pending") {
      const currentStatusOption = officeBearerOptions.find(
        (option) => option.value === row.applicationStatus
      );
      updateFormik.setValues({
        applicationStatus: currentStatusOption || "",
        remarks:
          row?.officeBearerRemarks?.filter(
            (item) => item?.addedBy?._id === user?._id
          )?.remark || "",
      });
      setSelectedRole(currentStatusOption || null);
    } else {
      updateFormik.resetForm();
      setSelectedRole(null);
    }

    setStatusDialog(true);
  };

  /* HANDLE STATUS DIALOG */
  const handleStatusDialogOpenJury = (row) => {
    setSelectedId(row?._id);
    const user = JSON.parse(localStorage.getItem("user"));

    if (row?.applicationStatus && row?.applicationStatus !== "pending") {
      const currentStatusOption = juryOptions.find(
        (option) => option.value === row.applicationStatus
      );
      updateFormikJury.setValues({
        applicationStatus: currentStatusOption || "",
        remarks:
          row?.juryRemarks?.filter((item) => item?.addedBy?._id === user?._id)
            ?.remark || "",
      });
      setSelectedRole(currentStatusOption || null);
    } else {
      updateFormikJury.resetForm();
      setSelectedRole(null);
    }

    setJuryStatusDialog(true);
  };

  /* HANDLE STATUS DIALOG CLOSE */
  const handleStatusDialogClose = () => {
    setStatusDialog(false);
    updateFormik.resetForm();
    setSelectedRole(null);
    setSelectedId(null);
  };

  /* HANDLE STATUS DIALOG CLOSE */
  const handleStatusDialogCloseJury = () => {
    setJuryStatusDialog(false);
    updateFormikJury.resetForm();
    setSelectedRole(null);
    setSelectedId(null);
  };

  /* COLUMNS */
  const columns = [
    {
      name: "Sr. No",
      grow: 0.5,
      selector: (row, index) => (
        <span className="text-sm font-ibm-regular text-black text-opacity-60">
          {index + 1}
        </span>
      ),
    },
    {
      name: "Applicant Name",
      grow: 1.5,
      selector: (row) => (
        <div className="flex flex-col py-2">
          <p className="text-sm font-ibm-semibold text-black">
            {getInstituteName(row)}
          </p>
        </div>
      ),
    },
    {
      name: "Email & Mobile",
      grow: 1.5,
      selector: (row) => {
        return (
          <div className="flex flex-col gap-1 mt-1">
            <a
              href={`mailto:${row.email}`}
              className="text-sm text-blue-500 hover:underline"
            >
              {row.institute.email}
            </a>
            <a
              href={`tel:${row.institute.mobileNumber}`}
              className="text-sm text-blue-500 hover:underline"
            >
              {row.institute.mobileNumber}
            </a>
          </div>
        );
      },
    },
    {
      name: "Status",
      grow: 0.8,
      selector: (row) => {
        return (
          <div className="p-0">
            {row?.applicationStatus === "eligible" ? (
              <div className="w-fit bg-green-500/10 border border-green-500/50 text-green-600 text-sm font-ibm-regular px-3 py-1 rounded-md">
                Eligible
              </div>
            ) : row?.applicationStatus === "ineligible" ? (
              <div className="w-fit bg-orange-500/10 border border-orange-500/50 text-orange-600 text-sm font-ibm-regular px-3 py-1 rounded-md">
                Ineligible
              </div>
            ) : row?.applicationStatus === "rejected" ? (
              <div className="w-fit bg-red-500/10 border border-red-500/50 text-red-600 text-sm font-ibm-regular px-3 py-1 rounded-md">
                Rejected
              </div>
            ) : row?.applicationStatus === "pending" ? (
              <div className="w-fit bg-greyBg/50 border border-greyBorder text-greyText text-sm font-ibm-regular px-3 py-1 rounded-md">
                Pending
              </div>
            ) : row?.applicationStatus === "selected" ? (
              <div className="w-fit bg-green-500 border border-green-500 text-white text-sm font-ibm-regular px-3 py-1 rounded-md">
                Selected
              </div>
            ) : row?.applicationStatus === "not-selected" ? (
              <div className="w-fit bg-red-500 border border-red-500 text-white text-sm font-ibm-regular px-3 py-1 rounded-md">
                Not selected
              </div>
            ) : (
              <></>
            )}
            {row?.isShortListed && (
              <div className="mt-2 bg-logoRed/10 border border-logoRed text-logoRed text-sm font-ibm-regular px-3 py-1 rounded-md">
                Shortlisted
              </div>
            )}
          </div>
        );
      },
    },
    {
      name: "Date Applied",
      grow: 0.8,
      selector: (row) => (
        <div>
          <p className="text-sm font-ibm-regular">
            {formatDate(row.dateOfApplication)}
          </p>
        </div>
      ),
    },
    {
      name: "Actions",
      grow: 0.5,
      selector: (row) => (
        <Menu>
          <MenuHandler>
            <Button className="shadow-none hover:shadow-none p-1.5 bg-greyBg border border-greyBorder hover:border-black/40 rounded-full transition-all duration-300 ease-in-out">
              <GoKebabHorizontal className="w-4 h-4 text-greyText rotate-90" />
            </Button>
          </MenuHandler>
          <MenuList className="p-1.5 min-w-[20vh] bg-white">
            {userRole === "OFFICE-BEARER" && (
              <MenuItem
                onClick={() => handleStatusDialogOpen(row)}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                Update status
              </MenuItem>
            )}
            {userRole === "JURY" && (
              <MenuItem
                onClick={() => handleStatusDialogOpenJury(row)}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                Update status
              </MenuItem>
            )}
            {row?.officeBearerRemarks?.length > 0 && (
              <MenuItem
                onClick={() => {
                  setRewardsDialog(!rewardsDialog);
                  setSelectedUserDetails(row);
                }}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                View remarks
              </MenuItem>
            )}
            {/* EDIT APPLICATION */}
            {userRole === "OFFICE-BEARER" && (
              <MenuItem
                onClick={() => {
                  setSelectedApplication(row);

                  setEditModal(true);
                }}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                Edit application
              </MenuItem>
            )}
            <MenuItem
              onClick={() => viewApplicationDetails(row)}
              className="text-sm font-ibm-regular px-4 py-2 text-black"
            >
              View details
            </MenuItem>
            <MenuItem
              onClick={() => {
                setSelectedApplication(row);
                setDetailsModal(true);
                setTimeout(() => {
                  exportInstitutePDF(row);
                }, 500);
              }}
              className="text-sm font-ibm-regular px-4 py-2 text-black"
            >
              Export to PDF
            </MenuItem>
            {userRole === "OFFICE-BEARER" &&
            row?.applicationStatus === "selected" ? (
              <MenuItem
                onClick={() => {
                  setSendEmailDialog(!sendEmailDialog);
                  setDeleteId(row?._id);
                }}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                Send email to institute
              </MenuItem>
            ) : (
              <></>
            )}
            {userRole === "OFFICE-BEARER" && (
              <MenuItem
                onClick={() => {
                  setDeleteId(row?._id);
                  setDeleteModal(!deleteModal);
                }}
                className="text-sm font-ibm-regular px-4 py-2 text-red-400"
              >
                Delete application
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      ),
    },
  ];

  /* USE-EFFECTS */
  useEffect(() => {
    if (userRole !== "" && defaultSelected !== null) {
      getDataListFunction();
    }
  }, [
    page,
    sizePerPage,
    debouncedSearch,
    selectedState,
    selectedZone,
    defaultSelected,
  ]);

  /* USE-EFFECTS */
  useEffect(() => {
    const formattedOptions = [
      { value: null, label: "All states" },
      ...statesOfIndia.map((state) => ({
        value: state,
        label: state,
      })),
    ];

    setStateOptions(formattedOptions);
  }, []);

  /* USE-EFFECTS */
  useEffect(() => {
    const formattedOptions = [
      { value: null, label: "All zones" },
      ...zones.map((zone) => ({
        value: zone,
        label: zone,
      })),
    ];

    setZoneOptions(formattedOptions);
  }, []);

  /* LOADING SCREEN FOR DATATABLE */
  const LoadingScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-regular text-black text-center">
          Please wait!
        </p>
        <p className="text-sm font-ibm-regular text-black text-center">
          We are fetching data{" "}
        </p>
      </div>
    );
  };

  /* NO DATA SCREEN */
  const NoDataScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-regular text-black text-center">
          There are no institutes to display.
        </p>
      </div>
    );
  };

  /* RENDER DETAIL FIELD */
  const renderDetailField = (label, value, isLink = false) => {
    // Check if it's a file and determine if it's an image
    const isImage =
      isLink &&
      value &&
      /\.(jpg|jpeg|png|gif|webp)$/i.test(value.toLowerCase());

    // Get filename from path for download attribute
    const getFilenameFromPath = (path) => {
      if (!path) return "";
      // Extract filename from path
      const parts = path.split("/");
      return parts[parts.length - 1];
    };

    return (
      <div className="mb-5 group">
        <p className="text-sm font-ibm-regular text-greyText mb-1">{label}</p>
        {isLink && value ? (
          isImage && label !== "Signature" ? (
            <div className="w-fit mb-2 relative">
              <img
                src={getFullFileUrl(value)}
                alt={label}
                className="max-h-40 object-contain border border-gray-200 rounded-md shadow-sm hover:shadow-md transition-shadow duration-200 aspect-square bg-white"
              />
              <div className="absolute top-1 right-1">
                <div className="flex items-center gap-1">
                  <a
                    href={getFullFileUrl(value)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-red-600 hover:text-red-800 mt-1 border border-red-100 px-2 py-1 rounded-md bg-red-50 flex items-center gap-2 font-ibm-regular"
                  >
                    <FaDownload className="w-3 h-3 text-red-700" /> View
                  </a>
                </div>
              </div>
            </div>
          ) : isImage && label === "Signature" ? (
            <div className="w-fit mb-2 relative">
              <img
                src={getFullFileUrl(value)}
                alt="Signature"
                className="max-h-40 w-[30vh] object-contain rounded-md bg-white"
                style={{ pointerEvents: "none" }}
                onContextMenu={(e) => e.preventDefault()}
                draggable={false}
              />
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <a
                href={getFullFileUrl(value)}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-red-600 hover:text-red-800 hover:underline flex items-center gap-1 transition-colors duration-200 bg-red-50 px-4 py-2 rounded-md border border-red-100 font-ibm-regular"
              >
                <FaDownload size={12} className="text-red-700" /> View Document
              </a>
            </div>
          )
        ) : (
          <p className="text-sm bg-greyBg border border-greyBorder text-black px-3 py-2 rounded-md transition-all duration-200 font-ibm-regular">
            {value || "N/A"}
          </p>
        )}
      </div>
    );
  };

  /* RENDER EDITABLE FIELDS */
  const renderEditField = (label, name, type = "text") => (
    <div className="mb-0">
      <p className="text-sm font-ibm-regular text-greyText mb-1">{label}</p>
      <Input
        name={name}
        type={type}
        value={editFormik.values[name]}
        onChange={editFormik.handleChange}
        className="w-full"
      />
    </div>
  );

  /* RENDER TEXTAREA */
  const renderTextareaField = (label, name, rows = "3") => (
    <div className="mb-0">
      <p className="text-sm font-ibm-regular text-greyText mb-1">{label}</p>
      <textarea
        rows={rows}
        name={name}
        value={editFormik.values[name]}
        onChange={editFormik.handleChange}
        className="w-full block px-3 py-2 rounded-md border border-authBorder text-sm font-ibm-regular text-black placeholder:text-sm placeholder:text-gray-500 placeholder:font-ibm-regular focus:outline-none"
      />
    </div>
  );

  /* RENDER DROPDOWN */
  const renderDropdownField = (label, name, options, firstOption) => (
    <div className="mb-0">
      <p className="text-sm font-ibm-regular text-greyText mb-1">{label}</p>
      <div className="px-3 py-2 rounded-md border border-authBorder">
        <select
          name={name}
          value={editFormik.values[name]}
          onChange={editFormik.handleChange}
          className="w-full block text-sm font-ibm-regular text-black focus:outline-none"
        >
          <option value="">{firstOption}</option>
          {options.map((program) => (
            <option key={program.value} value={program.value}>
              {program.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  /* RENDER FILE FIELDS */
  const renderFileField = (label, name) => (
    <div className="mb-5">
      <p className="text-sm font-ibm-regular text-greyText mb-1">{label}</p>
      <input
        type="file"
        name={name}
        onChange={(e) => editFormik.setFieldValue(name, e.target.files[0])}
        className="w-full font-ibm-regular"
      />
      {editFormik.values[name] &&
        typeof editFormik.values[name] === "string" && (
          <a
            href={getFullFileUrl(editFormik.values[name])}
            target="_blank"
            rel="noreferrer"
            className="mt-2 text-sm text-blue-500 hover:underline block font-ibm-regular underline"
          >
            View current uploaded file
          </a>
        )}
    </div>
  );

  /* EDIT FORMIK */
  const editFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      /* CONTACT PERSON'S INFORMATION */
      firstName: selectedApplication?.institute?.firstName,
      middleName: selectedApplication?.institute?.middleName,
      lastName: selectedApplication?.institute?.lastName,
      state: selectedApplication?.institute?.state,
      district: selectedApplication?.institute?.district,
      zone: selectedApplication?.institute?.zone,
      mahajan: selectedApplication?.institute?.mahajan,
      email: selectedApplication?.institute?.email,
      mobileNumber: selectedApplication?.institute?.mobileNumber,
      lohanaConnectId: selectedApplication?.lohanaConnectId,
      photograph: selectedApplication?.institute?.photograph,
      applicantSignature: selectedApplication?.applicantSignature,

      /* INSTITUTE INFORMATION */
      instituteName: selectedApplication?.institute?.name,
      accreditation: selectedApplication?.institute?.accreditation,
      achievements: selectedApplication?.institute?.achievements,
      affiliatingBody: selectedApplication?.institute?.affiliatingBody,
      campusAddress: selectedApplication?.institute?.campusAddress,
      dateOfEstablishment: moment(
        selectedApplication?.institute?.dateOfEstablishment
      ).format("YYYY-MM-DD"),
      detailedProfile: selectedApplication?.institute?.detailedProfile,
      officeAddress: selectedApplication?.institute?.officeAddress,
      numberOfStudentsStudying:
        selectedApplication?.institute?.numberOfStudentsStudying,
      profileSummary: selectedApplication?.institute?.profileSummary,
      typeOfInstitute: selectedApplication?.institute?.typeOfInstitute,
      youtubeURL: selectedApplication?.institute?.youtubeURL,

      /* SUPPORTING DOCUMENTS */
      copyOfRegistrationCertificate:
        selectedApplication?.documents?.copyOfRegistrationCertificate,
    },
    onSubmit: (values) => {
      setFormLoading(true);
      const formData = new FormData();

      /* PERSONAL INFORMATION */
      formData.append("institute.firstName", values.firstName);
      formData.append("institute.middleName", values.middleName);
      formData.append("institute.lastName", values.lastName);
      formData.append("institute.state", values.state);
      formData.append("institute.district", values.district);
      formData.append("institute.zone", values.zone);
      formData.append("institute.mahajan", values.mahajan);
      formData.append("institute.email", values.email);
      formData.append("institute.mobileNumber", values.mobileNumber);
      formData.append("lohanaConnectId", values.lohanaConnectId);
      formData.append("institute.photograph", values.photograph);
      formData.append("applicantSignature", values.applicantSignature);

      /* PROFESSIONAL DETAILS */
      formData.append("institute.name", values.instituteName);
      formData.append("institute.accreditation", values.accreditation);
      formData.append("institute.achievements", values.achievements);
      formData.append("institute.affiliatingBody", values.affiliatingBody);
      formData.append("institute.campusAddress", values.campusAddress);
      formData.append(
        "institute.dateOfEstablishment",
        values.dateOfEstablishment
      );
      formData.append("institute.detailedProfile", values.detailedProfile);
      formData.append("institute.officeAddress", values.officeAddress);
      formData.append(
        "institute.numberOfStudentsStudying",
        values.numberOfStudentsStudying
      );
      formData.append("institute.profileSummary", values.profileSummary);
      formData.append("institute.typeOfInstitute", values.typeOfInstitute);
      formData.append("institute.youtubeURL", values.youtubeURL);

      /* SUPPORTING DOCUMENTS */
      formData.append(
        "documents.copyOfRegistrationCertificate",
        values.copyOfRegistrationCertificate
      );

      updateApplication(selectedApplication?._id, formData)
        .then((res) => {
          if (res?.status) {
            toast.success("The application has been updated successfully");
            setEditModal(false);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          console.log("Error: ", e);
        })
        .finally(() => {
          setFormLoading(false);
          getDataListFunction();
        });
    },
  });

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      const userData = JSON.parse(user);
      setUserRole(userData.designation);
      if (userData.designation === "OFFICE-BEARER") {
        setDefaultSelected({ value: null, label: "All status" });
      } else {
        setDefaultSelected({ value: "eligible", label: "Eligible" });
      }
    }
  }, []);

  return (
    <section className="min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Institute Applications | Admin Panel</title>
      </Helmet>

      <div className="container-full-width">
        {/* HEADING */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
              Institute Applications
            </h1>
            <h2 className="text-base font-ibm-regular text-greyText">
              Manage and view Institute applications
            </h2>
          </div>
          <div className="w-full md:w-auto mt-2 md:mt-0 flex items-center gap-2">
            {/* EXCEL EXPORT BUTTON */}
            <ExcelExport
              data={exportData}
              fileName="Institute Applications"
              btnText="Export data"
              onClickFn={async () => {
                try {
                  return await getExportList();
                } catch (error) {
                  console.error("Failed to prepare export data:", error);
                  throw error;
                }
              }}
            />

            {/* SEARCHBAR */}
            <div className="w-full md:w-[250px]">
              <Input
                type="text"
                placeholder="Search by institute name , email."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* FILTERS */}
        <div className="mt-5 flex items-center gap-3 justify-end">
          {/* FILTER BY STATUS */}
          {userRole === "SUPER-ADMIN" ? (
            <div className="w-full md:w-[200px]">
              <Select
                options={statusOptionsForSuperAdmin}
                className="react-select"
                classNamePrefix="dropdown"
                value={defaultSelected}
                onChange={(e) => setDefaultSelected(e)}
                placeholder="Filter by state"
              />
            </div>
          ) : (
            <div className="w-full md:w-[200px]">
              <Select
                options={
                  userRole === "OFFICE-BEARER"
                    ? statusOptionsForOfficeBearer
                    : statusOptionsForJury
                }
                className="react-select"
                classNamePrefix="dropdown"
                value={defaultSelected}
                onChange={(e) => setDefaultSelected(e)}
                placeholder="Filter by state"
              />
            </div>
          )}

          {/* FILTER BY ZONE */}
          <div className="w-full md:w-[200px]">
            <Select
              options={zoneOptions}
              className="react-select"
              classNamePrefix="dropdown"
              value={selectedZone}
              onChange={(e) => setSelectedZone(e)}
              placeholder={`${
                selectedZone === null ? "Select zone" : "Filter by zone"
              }`}
            />
          </div>

          {/* FILTER BY STATE */}
          <div className="w-full md:w-[200px]">
            <Select
              options={stateOptions}
              className="react-select"
              classNamePrefix="dropdown"
              value={selectedState}
              onChange={(e) => setSelectedState(e)}
              placeholder="Filter by state"
            />
          </div>
        </div>

        {/* DATA TABLE */}
        <div className="mt-5">
          <Card className="bg-white border border-greyBorder rounded-lg">
            <CardBody className="p-5">
              <DataTable
                columns={columns}
                data={dataList.filter((item) => item?.firstName !== "Testing")}
                progressPending={loading}
                progressComponent={<LoadingScreen />}
                noDataComponent={<NoDataScreen />}
                pagination
                paginationServer
                paginationTotalRows={totalDocs}
                onChangeRowsPerPage={handlePerPageChange}
                onChangePage={handlePageChange}
                className="data-table"
              />
            </CardBody>
          </Card>
        </div>
      </div>

      {/* DELETE MODAL */}
      <Dialog
        open={deleteModal}
        handler={() => setDeleteModal(!deleteModal)}
        size="xs"
      >
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-3 border-b border-greyBorder">
            <div className="flex items-center justify-between">
              <h5 className="text-base font-ibm-semibold text-black">
                Delete Application
              </h5>
              <Button
                onClick={() => setDeleteModal(!deleteModal)}
                className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
              >
                <IoClose className="w-5 h-5 text-black" />
              </Button>
            </div>
          </div>

          {/* BODY */}
          <div className="p-5">
            <p className="text-sm font-ibm-regular text-black">
              Are you sure you want to delete this institute application? This
              action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                onClick={() => setDeleteModal(!deleteModal)}
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5 text-sm font-ibm-regular text-black"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white"
              >
                Delete
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>

      {/* APPLICATION DETAILS MODAL */}
      <Dialog
        open={detailsModal}
        handler={() => setDetailsModal(!detailsModal)}
        size="lg"
        className="bg-white rounded-xl shadow-xl"
      >
        <DialogBody className="p-0">
          {/* {console.log("Selected Application Data:", selectedApplication)} */}
          {selectedApplication && (
            <>
              <div className="flex items-center justify-between px-5 py-3 border-b border-greyBorder rounded-t-xl">
                <h5 className="text-lg font-ibm-bold text-black">
                  Institute Application Details
                </h5>
                <Button
                  onClick={() => setDetailsModal(!detailsModal)}
                  className="p-2 rounded-full shadow-none hover:shadow-none bg-white hover:bg-red-50"
                >
                  <IoClose className="w-5 h-5 text-red-800" />
                </Button>
              </div>

              <div className="mt-0 max-h-[70vh] overflow-y-auto p-5">
                {/* PERSONAL INFORMATION */}
                <div className="border border-greyBorder rounded-lg p-5 mb-5 bg-white shadow">
                  <h3 className="text-md font-ibm-bold mb-4 text-logoRed border-b border-greyBorder pb-2">
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      {selectedApplication.dateOfApplication &&
                        renderDetailField(
                          "Application Date",
                          formatDate(selectedApplication.dateOfApplication)
                        )}
                      {selectedApplication.lohanaConnectId &&
                        renderDetailField(
                          "Lohana Connect ID",
                          selectedApplication.lohanaConnectId
                        )}
                      {selectedApplication.applicantSignature &&
                        renderDetailField(
                          "Signature",
                          selectedApplication.applicantSignature,
                          true
                        )}
                    </div>
                  </div>
                </div>

                {/* INSTITUTE INFORMATION */}
                {selectedApplication.institute && (
                  <div className="border border-greyBorder rounded-lg p-5 mb-5 bg-white shadow">
                    <h3 className="text-md font-ibm-bold mb-4 text-logoRed border-b border-greyBorder pb-2">
                      Institute Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        {selectedApplication.institute &&
                          renderDetailField(
                            "Institute Name",
                            selectedApplication.institute.name
                          )}
                        {selectedApplication.institute &&
                          renderDetailField(
                            "Contact Person's Full Name",
                            getInstituteName(selectedApplication)
                          )}
                        {selectedApplication.institute &&
                          renderDetailField(
                            "Date of Establishment",
                            selectedApplication.institute.dateOfEstablishment
                              ? formatDate(
                                  selectedApplication.institute
                                    .dateOfEstablishment
                                )
                              : "N/A"
                          )}
                        {selectedApplication.institute &&
                          renderDetailField(
                            "Mobile Number",
                            selectedApplication.institute.mobileNumber
                          )}
                        {selectedApplication.institute &&
                          selectedApplication.institute.detailedProfile &&
                          renderDetailField(
                            "Detailed Profile",
                            selectedApplication.institute.detailedProfile,
                            true
                          )}
                        {selectedApplication.institute &&
                          selectedApplication.institute.photograph &&
                          renderDetailField(
                            "Photograph",
                            selectedApplication.institute.photograph,
                            true
                          )}
                      </div>
                      <div>
                        {selectedApplication.institute &&
                          renderDetailField(
                            "Email",
                            selectedApplication.institute.email
                          )}
                        {selectedApplication.institute &&
                          renderDetailField(
                            "Office Address",
                            selectedApplication.institute.officeAddress
                          )}
                        {selectedApplication.institute &&
                          renderDetailField(
                            "Campus Address",
                            selectedApplication.institute.campusAddress
                          )}
                        {selectedApplication.institute &&
                          renderDetailField(
                            "State",
                            selectedApplication.institute?.state || "N/A"
                          )}
                        {selectedApplication.institute &&
                          renderDetailField(
                            "District",
                            selectedApplication.institute?.district || "N/A"
                          )}
                        {selectedApplication.institute &&
                          renderDetailField(
                            "Zone",
                            selectedApplication.institute.zone
                          )}
                        {selectedApplication.institute &&
                          renderDetailField(
                            "Mahajan",
                            selectedApplication.institute.mahajan
                          )}
                        {selectedApplication.institute &&
                          renderDetailField(
                            "Type of Institute",
                            selectedApplication.institute.typeOfInstitute
                          )}
                        {selectedApplication.institute &&
                          renderDetailField(
                            "Affiliating Body",
                            selectedApplication.institute.affiliatingBody
                          )}
                        {selectedApplication.institute &&
                          renderDetailField(
                            "Accreditation",
                            selectedApplication.institute.accreditation
                          )}
                        {selectedApplication.institute &&
                          renderDetailField(
                            "Number of Students",
                            selectedApplication.institute
                              .numberOfStudentsStudying
                          )}
                        {selectedApplication.institute &&
                          renderDetailField(
                            "Institute Facilities Youtube URL",
                            selectedApplication.institute.youtubeURL
                          )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Documents */}
                {selectedApplication.documents && (
                  <div className="border border-greyBorder rounded-lg p-5 bg-white shadow">
                    <h3 className="text-md font-ibm-bold mb-4 text-logoRed border-b border-greyBorder pb-2">
                      Supporting Documents
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-1 gap-5">
                      {selectedApplication.documents &&
                      selectedApplication.documents
                        .copyOfRegistrationCertificate
                        ? renderDetailField(
                            "Copy of Registration Certificate",
                            selectedApplication.documents
                              .copyOfRegistrationCertificate,
                            true
                          )
                        : null}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogBody>
      </Dialog>

      {/* STATUS DIALOG - OFFICE BEARER */}
      <Dialog open={statusDialog} handler={handleStatusDialogClose} size="xs">
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-3 border-b border-greyBorder flex items-center justify-between">
            <h5 className="text-base font-ibm-semibold text-black">
              Update status
            </h5>
            <Button
              onClick={handleStatusDialogClose}
              className="shadow-none hover:shadow-none bg-white hover:bg-black/10 rounded-full p-1 transition-all duration-300 ease-in-out"
            >
              <IoClose className="w-5 h-5 text-greyText" />
            </Button>
          </div>

          {/* BODY */}
          <form onSubmit={updateFormik.handleSubmit} className="p-5">
            {/* STATUS */}
            <div className="p-0">
              <p className="text-sm font-ibm-regular text-greyText">
                Please select the status of the application
              </p>
              <div className="mt-1">
                <Select
                  options={
                    isShortlistedValue === "eligible"
                      ? officeBearerOptionsUpdated
                      : officeBearerOptions
                  }
                  className="react-select"
                  value={updateFormik.values.applicationStatus}
                  onChange={(e) => {
                    updateFormik.setFieldValue("applicationStatus", e);
                    setSelectedRole(e);
                  }}
                  classNamePrefix="dropdown"
                  placeholder="Select role"
                />
              </div>
            </div>

            {/* REMARKS */}
            {selectedRole !== null && selectedRole?.label !== "Shortlisted" && (
              <div className="mt-5">
                <label className="text-sm font-ibm-regular text-greyText">
                  {selectedRole?.label === "Eligible" ||
                  selectedRole?.label === "Ineligible"
                    ? "Any remarks"
                    : "Please enter the reason of rejection"}
                </label>
                <div className="mt-1">
                  <textarea
                    rows={3}
                    placeholder={
                      selectedRole?.label === "Eligible" ||
                      selectedRole?.label === "Ineligible"
                        ? "Please enter the remarks"
                        : "Enter the reason of rejection"
                    }
                    name="remarks"
                    value={updateFormik.values.remarks}
                    onChange={updateFormik.handleChange}
                    className="block w-full px-5 py-3 rounded-md border border-authBorder  text-sm font-ibm-regular text-black placeholder:text-sm placeholder:text-gray-500 placeholder:font-ibm-regular focus:outline-none"
                  />
                </div>
              </div>
            )}
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                type="submit"
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white"
              >
                Update
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>

      {/* STATUS DIALOG - JURY */}
      <Dialog
        open={juryStatusDialog}
        handler={handleStatusDialogCloseJury}
        size="xs"
      >
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-3 border-b border-greyBorder flex items-center justify-between">
            <h5 className="text-base font-ibm-semibold text-black">
              Update status
            </h5>
            <Button
              onClick={handleStatusDialogCloseJury}
              className="shadow-none hover:shadow-none bg-white hover:bg-black/10 rounded-full p-1 transition-all duration-300 ease-in-out"
            >
              <IoClose className="w-5 h-5 text-greyText" />
            </Button>
          </div>

          {/* BODY */}
          <form onSubmit={updateFormikJury.handleSubmit} className="p-5">
            {/* STATUS */}
            <div className="p-0">
              <p className="text-sm font-ibm-regular text-greyText">
                Please select the status of the application
              </p>
              <div className="mt-1">
                <Select
                  options={juryOptions}
                  className="react-select"
                  value={updateFormikJury.values.applicationStatus}
                  onChange={(e) => {
                    updateFormikJury.setFieldValue("applicationStatus", e);
                    setSelectedRole(e);
                  }}
                  classNamePrefix="dropdown"
                  placeholder="Select status"
                />
              </div>
            </div>

            {/* REMARKS */}
            {selectedRole !== null && (
              <div className="mt-5">
                <label className="text-sm font-ibm-regular text-greyText">
                  Remarks (if any)
                </label>
                <div className="mt-1">
                  <textarea
                    rows={3}
                    placeholder="Please enter the remarks"
                    name="remarks"
                    value={updateFormikJury.values.remarks}
                    onChange={updateFormikJury.handleChange}
                    className="block w-full px-5 py-3 rounded-md border border-authBorder  text-sm font-ibm-regular text-black placeholder:text-sm placeholder:text-gray-500 placeholder:font-ibm-regular focus:outline-none"
                  />
                </div>
              </div>
            )}
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                type="submit"
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white"
              >
                Update
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>

      {/* REWARDS DIALOG */}
      <Dialog
        open={rewardsDialog}
        handler={() => setRewardsDialog(!rewardsDialog)}
        size="md"
      >
        <DialogBody className="p-5 relative max-h-[80vh] overflow-auto hide-scrollbar">
          {/* OFFICE BEARER REMARKS */}
          <div className="pb-0">
            <h5 className="text-md font-ibm-semibold text-black">
              Office Bearer Remarks
            </h5>
            <div className="mt-2">
              {selectedUserDetails?.officeBearerRemarks?.map((item, index) => (
                <div
                  key={index}
                  className="mt-5 first:mt-0 bg-white border border-greyBorder rounded-lg shadow p-3"
                >
                  <div className="flex items-center justify-between pb-2 border-b border-greyBorder">
                    <div className="p-0">
                      <p className="text-black font-ibm-semibold text-base">
                        {item?.addedBy?.firstName} {item?.addedBy?.lastName}
                      </p>
                      <p className="text-sm font-ibm-regular text-greyText">
                        {moment(item?.createdAt).format("DD MMMM, HH:mm A")}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {item?.applicationStatus === "eligible" ? (
                        <div className="bg-green-500/10 border border-green-500/50 text-green-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Eligible
                        </div>
                      ) : item?.applicationStatus === "ineligible" ? (
                        <div className="bg-orange-500/10 border border-orange-500/50 text-orange-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Ineligible
                        </div>
                      ) : item?.applicationStatus === "rejected" ? (
                        <div className="bg-red-500/10 border border-red-500/50 text-red-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Rejected
                        </div>
                      ) : item?.applicationStatus === "pending" ? (
                        <div className="bg-greyBg/50 border border-greyBorder text-greyText text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Pending
                        </div>
                      ) : item?.applicationStatus === "selected" ? (
                        <div className="bg-green-500 border border-green-500 text-white text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Selected
                        </div>
                      ) : item?.applicationStatus === "not-selected" ? (
                        <div className="bg-red-500 border border-red-500 text-white text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Not selected
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>

                  <div className="pt-3">
                    <p className="text-black font-ibm-regular">
                      {item?.remark}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* JURY REMARKS */}
          {selectedUserDetails?.juryRemarks?.length > 0 && (
            <div className="pt-5">
              <h5 className="text-md font-ibm-semibold text-black">
                Jury Remarks
              </h5>
              <div className="mt-2">
                {selectedUserDetails?.juryRemarks?.map((item, index) => (
                  <div
                    key={index}
                    className="mt-5 first:mt-0 bg-white border border-greyBorder rounded-lg shadow p-3"
                  >
                    <div className="flex items-center justify-between pb-2 border-b border-greyBorder">
                      <div className="p-0">
                        <p className="text-black font-ibm-semibold text-base">
                          {item?.addedBy?.firstName} {item?.addedBy?.lastName}
                        </p>
                        <p className="text-sm font-ibm-regular text-greyText">
                          {moment(item?.createdAt).format("DD MMMM, HH:mm A")}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {item?.applicationStatus === "eligible" ? (
                          <div className="bg-green-500/10 border border-green-500/50 text-green-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Eligible
                          </div>
                        ) : item?.applicationStatus === "ineligible" ? (
                          <div className="bg-orange-500/10 border border-orange-500/50 text-orange-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Ineligible
                          </div>
                        ) : item?.applicationStatus === "rejected" ? (
                          <div className="bg-red-500/10 border border-red-500/50 text-red-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Rejected
                          </div>
                        ) : item?.applicationStatus === "pending" ? (
                          <div className="bg-greyBg/50 border border-greyBorder text-greyText text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Pending
                          </div>
                        ) : item?.applicationStatus === "selected" ? (
                          <div className="bg-green-500 border border-green-500 text-white text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Selected
                          </div>
                        ) : item?.applicationStatus === "not-selected" ? (
                          <div className="bg-red-500 border border-red-500 text-white text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Not selected
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>

                    <div className="pt-3">
                      <p className="text-black font-ibm-regular">
                        {item?.remark}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="absolute top-3 right-3">
            <Button
              onClick={() => setRewardsDialog(!rewardsDialog)}
              className="shadow-none hover:shadow-none bg-white hover:bg-black/10 rounded-full p-1 transition-all duration-300 ease-in-out"
            >
              <IoClose className="w-5 h-5 text-greyText" />
            </Button>
          </div>
        </DialogBody>
      </Dialog>

      {/* DELETE MODAL */}
      <Dialog
        open={sendEmailDialog}
        handler={() => setSendEmailDialog(!sendEmailDialog)}
        size="xs"
      >
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-3 border-b border-greyBorder">
            <div className="flex items-center justify-between">
              <h5 className="text-base font-ibm-semibold text-black">
                Confirmation
              </h5>
              <Button
                onClick={() => setSendEmailDialog(!sendEmailDialog)}
                className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
              >
                <IoClose className="w-5 h-5 text-black" />
              </Button>
            </div>
          </div>

          {/* BODY */}
          <div className="p-5">
            <p className="text-sm font-ibm-regular text-black">
              Are you sure you want to send email to this applicant? This action
              cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                onClick={() => setSendEmailDialog(!sendEmailDialog)}
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5 text-sm font-ibm-regular text-black"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendEmail}
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white"
              >
                Confirm
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>

      {/* EDIT MODAL */}
      <Dialog
        open={editModal}
        handler={() => setEditModal(!editModal)}
        size="lg"
        className="bg-white rounded-xl shadow-xl"
      >
        <DialogBody className="p-0">
          <form onSubmit={editFormik.handleSubmit} className="p-0">
            {/* HEADING */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-greyBorder rounded-t-xl">
              <h5 className="text-lg font-ibm-bold text-black">
                Institute Application Details
              </h5>
              <Button
                onClick={() => setEditModal(!editModal)}
                className="p-2 rounded-full shadow-none hover:shadow-none bg-white hover:bg-red-50"
              >
                <IoClose className="w-5 h-5 text-red-800" />
              </Button>
            </div>

            <div className="mt-0 max-h-[70vh] overflow-y-auto p-5">
              {/* PERSONAL INFORMATION */}
              <div className="border border-greyBorder rounded-lg p-5 mb-5 bg-white shadow">
                <h3 className="text-md font-ibm-bold mb-4 text-logoRed border-b border-greyBorder pb-2">
                  Contact Person's Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {renderEditField("First name", "firstName")}
                  {renderEditField("Middle name", "middleName")}
                  {renderEditField("Last name", "lastName")}
                </div>
                <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
                  {renderEditField("Email address", "email")}
                  {renderEditField("Mobile number", "mobileNumber")}
                  {renderEditField("Lohana Connect ID", "lohanaConnectId")}
                </div>
                <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-5">
                  {renderEditField("State", "state")}
                  {renderEditField("District", "district")}
                  {renderEditField("Zone", "zone")}
                  {renderEditField("Mahajan", "mahajan")}
                </div>
              </div>

              {/* INSTITUTE INFORMATION */}
              <div className="border border-greyBorder rounded-lg p-5 mb-5 bg-white shadow">
                <h3 className="text-md font-ibm-bold mb-4 text-logoRed border-b border-greyBorder pb-2">
                  Institute Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderEditField("Institute Name", "instituteName")}
                  {renderEditField(
                    "Date of establishment",
                    "dateOfEstablishment"
                  )}
                </div>
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderDropdownField(
                    "Type of institute",
                    "typeOfInstitute",
                    types
                  )}
                  {renderEditField(
                    "Institution Infrastructure Facilities",
                    "youtubeURL"
                  )}
                </div>
                <div className="mt-5 grid grid-cols-3 gap-5">
                  {renderEditField("Affiliating Body", "affiliatingBody")}
                  {renderEditField("Accreditation", "accreditation")}
                  {renderEditField(
                    "No. of students studying",
                    "numberOfStudentsStudying"
                  )}
                </div>
                <div className="mt-5 grid grid-cols-2 gap-5">
                  {renderTextareaField("Office address", "officeAddress")}
                  {renderTextareaField("Campus address", "campusAddress")}
                </div>
                <div className="mt-5">
                  {renderTextareaField("Summary of profile", "profileSummary")}
                </div>
                <div className="mt-5">
                  {renderTextareaField("Achievements", "achievements")}
                </div>
                <div className="mt-5">
                  {renderFileField("Photographs", "photograph")}
                </div>
              </div>

              {/* DOCUMENTS */}
              <div className="border border-greyBorder rounded-lg p-5 bg-white shadow">
                <h3 className="text-md font-ibm-bold mb-4 text-logoRed border-b border-greyBorder pb-2">
                  Supporting Documents
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-1 gap-5">
                  {renderFileField(
                    "Copy of Registration Certificate",
                    "copyOfRegistrationCertificate",
                    true
                  )}
                </div>
              </div>
            </div>

            {/* FOOTER */}
            <div className="flex justify-end gap-2 p-5 border-t border-greyBorder">
              <Button
                onClick={() => setEditModal(false)}
                className="bg-greyBg text-black shadow-none hover:shadow-none font-ibm-semibold text-base"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-logoRed text-white shadow-none hover:shadow-none font-ibm-semibold text-base"
              >
                {formLoading ? "Updating..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>
    </section>
  );
};

export default Institute;
